<template>
	<div
		ref="container"
		class="secure-password__container"
	>
		<div class="secure-password__main-panel">
			<Header
				display-icon
				icon-location="center"
				class="secure-password__header"
			/>
			<div class="secure-password__page-content">
				<div class="secure-password__main">
					<div class=" secure-password__info">
						<div class="secure-password__title">
							{{ translation['new-password'] }}
						</div>
						<div class="secure-password__desc">
							{{ translation['password-message'] }}
						</div>
						<div class="secure-password__content">
							<div class="secure-password__label">
								{{ translation['new-password'] }}
							</div>
							<helio-field
								:display-success="false"
								:initial-errors="specialCharacterErrors"
								type="password"
								@changed="password = $event"
								@blurred="validateSpecialCharacter"
								@keyup.enter.native="(!isButtonDisabled)?securePassword():()=>{}"
							/>
							<Validation
								:password="password"
								@validation="rulesRespected = $event"
								@special-character="specialCharacter = $event"
							/>
							<div class="secure-password__label">
								{{ translation['confirm-password'] }}
							</div>
							<helio-field
								:display-success="false"
								:initial-errors="errors"
								type="password"
								@changed="repeatedPassword = $event"
								@keyup.enter.native="(!isButtonDisabled)?securePassword():()=>{}"
							/>
							<div class="secure-password__save-button">
								<helio-button
									color="primary"
									size="large"
									:disabled="isButtonDisabled || animationEnabled"
									:state="buttonState"
									:text="translation['save']"
									@click="securePassword()"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</div>
		<SecurePasswordAnimation v-if="animationEnabled" />
	</div>
</template>

<script>
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Validation from '@/components/Validation';
import SecurePasswordAnimation from './SecurePasswordAnimation';
import { translationMixin, titleMixin } from '@/mixins';

const ERROR_CODE_INVALID_TOKEN = 401;
const ERROR_CODE_INVALID_PASSWORD = 422;

const ANIMATION_DURATION = 3000;

// TODO: Most of the logic is same as ResetPassword,
// use a better pattern to not maintain both separately.
export default {
	name: 'SecurePassword',
	components: {
		Header,
		Footer,
		Validation,
		SecurePasswordAnimation,
	},
	mixins: [translationMixin, titleMixin],
	data() {
		return {
			animationEnabled: false,
			rulesRespected: false,
			specialCharacter: false,
			password: '',
			repeatedPassword: '',
			error: null,
			resetToken: null,
			enableSecurePassword: true,
			specialCharacterErrors: [],
		};
	},
	computed: {
		translation() {
			return this.currentTranslation['secure-password'];
		},

		errors() {
			if (this.repeatedPassword.length > 0 && this.password !== this.repeatedPassword) {
				return [this.translation['passwords-do-not-match']];
			}

			return [];
		},

		isButtonDisabled() {
			return !this.rulesRespected || this.password !== this.repeatedPassword;
		},

		buttonState() {
			if (this.error.title.length > 1) {
				return 'is-unsuccessful';
			}
			if (!this.enableSecurePassword) {
				return 'is-loading';
			}
			return null;
		},
	},
	watch: {
		password() {
			this.specialCharacterErrors = [];
		},
	},
	created() {
		this.resetToken = this.$store.getters['application/getResetToken'];
		this.email = this.$store.getters['application/getEmail'];
		this.error = { title: '', description: '' };

		// Only display this page if the reset token is valid.
		if (this.resetToken == null || this.email == null) {
			this.$router.push({ name: `login-${this.$i18n.locale}` });
		}
	},
	methods: {
		validateSpecialCharacter() {
			setTimeout(() => {
				if (!this.specialCharacter && (this.password.includes('+') || this.password.includes('='))) {
					this.specialCharacterErrors = [this.translation['x-and-equal-not-special']];
				} else {
					this.specialCharacterErrors = [];
				}
			}, 50);
		},

		securePassword() {
			this.enableSecurePassword = false;
			this.$store.dispatch('sso/RESET_PASSWORD', {
				email: this.email,
				newPassword: this.password,
				resetToken: this.resetToken,
			})
				.then((response) => {
					const { code } = response.data;
					if (code === 200) {
						this.$store.dispatch('sso/LOGIN', {
							email: this.email,
							password: this.password,
						})
							.then(res => this.$store.dispatch('sso/SAVE_TOKENS', res.data.data))
							.then(() => {
								this.animationEnabled = true;
								setTimeout(() => {
									let redirectDomain = this.$store.getters['application/getRedirect'];
									if (redirectDomain == null) {
										redirectDomain = `${process.env.VUE_APP_CENTIVA_PROTOCOL}://${process.env.VUE_APP_CENTIVA_DOMAIN}`;
									}
									window.location = redirectDomain;
								}, ANIMATION_DURATION);
							});
					} else {
						this.animationEnabled = false;
						this.enableSecurePassword = true;
						throw new Error(code);
					}
				})
				.catch((error) => {
					this.enableSecurePassword = true;
					this.animationEnabled = false;
					try {
						const { code } = error.response.data;
						switch (code) {
							case ERROR_CODE_INVALID_TOKEN:
								this.error.title = this.translation['title-invalid-link'];
								this.error.description = this.translation['invalid-link'];
								break;
							case ERROR_CODE_INVALID_PASSWORD:
								this.error.title = this.translation['title-invalid-password'];
								this.error.description = this.translation['invalid-password'];
								break;
							default:
								this.error.title = this.translation['title-generic-error'];
								this.error.description = this.translation['generic-error'];
								break;
						}
					} catch (e) {
						this.error.title = this.translation['title-generic-error'];
						this.error.description = this.translation['generic-error'];
					}
				});
		},
	},
};
</script>

<style lang="scss" src="./secure-password.scss" />
